import {
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  ErrorPanel,
  LoadingPanel,
} from "@/components/ui/Loading";
import { Tooltip } from "@/components/ui/Tooltip";
import { useToast } from "@/components/ui/Toast";
import {
  billingTypeLabel,
  formatRate,
} from "@/domain/billing";
import { dueRuleLabel } from "@/domain/due-rules";
import type { ClientProfile } from "@/domain/types";
import { useAsyncResource } from "@/hooks/useAsyncResource";
import { apiRequest } from "@/lib/api";

export function ClientsPage() {
  const resource = useAsyncResource(() =>
    apiRequest<ClientProfile[]>(
      "/api/admin/clients",
    ),
  );

  const { showToast } = useToast();

  const remove = async (
    client: ClientProfile,
  ) => {
    if (!window.confirm(`Delete ${client.name}?`)) {
      return;
    }

    try {
      await apiRequest(
        `/api/admin/clients/${client.id}`,
        {
          method: "DELETE",
        },
      );

      showToast("Client profile deleted.");
      resource.reload();
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Could not delete client.",
        "error",
      );
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Client profiles"
        title="Clients"
        description="Store each client’s default currency, calculation method, rate unit, billing cycle, due schedule, and payment instructions. New invoices inherit these values but remain independently editable."
        actions={
          <Link
            to="/app/clients/new"
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" />
            New client
          </Link>
        }
      />

      {resource.loading ? (
        <LoadingPanel label="Loading clients…" />
      ) : resource.error ? (
        <ErrorPanel
          error={resource.error}
          onRetry={resource.reload}
        />
      ) : (resource.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Users}
          title="No client profiles yet"
          description="Create a profile to prefill future invoices with the correct agreement."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {resource.data!.map((client) => {
            const defaultRate =
              client.defaults.rateCents ??
              client.defaults.hourlyRateCents ??
              0;

            return (
              <Card
                key={client.id}
                className="p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate font-black text-ink">
                      {client.name}
                    </h2>
                    <p className="mt-1 truncate text-sm text-ink-muted">
                      {client.company ||
                        "Independent client"}
                    </p>
                  </div>

                  <div className="flex shrink-0 gap-1">
                    <Tooltip label="Edit client profile">
                      <Link
                        to={`/app/clients/${client.id}/edit`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-muted"
                      >
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Tooltip>

                    <Tooltip label="Delete client profile">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-9 min-h-9 w-9 text-danger-600"
                        onClick={() => remove(client)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl bg-surface-muted/70 p-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted">
                      Default rate
                    </p>
                    <p className="mt-1 break-words font-black text-ink">
                      {defaultRate > 0
                        ? formatRate(
                            defaultRate,
                            client.defaults.currency,
                            client.defaults.rateUnit ||
                              "unit",
                          )
                        : "Custom"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted">
                      Calculation
                    </p>
                    <p className="mt-1 font-semibold text-ink">
                      {billingTypeLabel(
                        client.defaults.billingType,
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted">
                      Cycle
                    </p>
                    <p className="mt-1 font-semibold capitalize text-ink">
                      {client.defaults.billingCycle.replace(
                        "_",
                        " ",
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted">
                      Currency
                    </p>
                    <p className="mt-1 font-semibold text-ink">
                      {client.defaults.currency}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-wider text-ink-muted">
                      Due arrangement
                    </p>
                    <p className="mt-1 font-semibold text-ink">
                      {dueRuleLabel(
                        client.defaults.dueRule,
                      )}
                    </p>
                  </div>
                </div>

                <div className="mt-4 text-xs text-ink-muted">
                  {client.email || "No email saved"}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
